import type { Point2D, Polygon2D } from '../gen/ejot/roofing/v1/types';

export type PolygonRing = readonly Point2D[];

/**
 * Reconstructs independent closed Polygon2D cycles from the authoritative
 * segment graph. The largest cycle is returned first as the outer ring and the
 * remaining cycles are holes. Legacy point-only polygons remain supported.
 */
export function polygonRings(polygon: Polygon2D | undefined): PolygonRing[] {
  const points = polygon?.points ?? [];
  if (points.length < 3) return [];
  const pointById = new Map(points.filter((point) => point.id).map((point) => [point.id!, point]));
  const segments = polygon?.segments ?? [];
  if (pointById.size !== points.length || segments.length < 3) return [points];

  const nextByStart = new Map<string, string>();
  for (const segment of segments) {
    const start = segment.start_point_id;
    const end = segment.end_point_id;
    if (!start || !end || !pointById.has(start) || !pointById.has(end) || nextByStart.has(start)) {
      return [points];
    }
    nextByStart.set(start, end);
  }

  const visited = new Set<string>();
  const rings: Point2D[][] = [];
  const starts = [...nextByStart.keys()].sort();
  for (const start of starts) {
    if (visited.has(start)) continue;
    const ring: Point2D[] = [];
    let current = start;
    for (let step = 0; step <= nextByStart.size; step += 1) {
      if (visited.has(current) && current !== start) return [points];
      const point = pointById.get(current);
      const next = nextByStart.get(current);
      if (!point || !next) return [points];
      ring.push(point);
      visited.add(current);
      current = next;
      if (current === start) break;
    }
    if (current !== start || ring.length < 3) return [points];
    rings.push(ring);
  }
  if (rings.length === 0) return [points];
  rings.sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)));
  return rings;
}

export function polygonSvgPath(polygon: Polygon2D | undefined): string {
  return polygonRings(polygon)
    .map((ring) => ring.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x ?? 0} ${point.y ?? 0}`).join(' ') + ' Z')
    .join(' ');
}

function signedArea(ring: PolygonRing): number {
  let twice = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    twice += (a.x ?? 0) * (b.y ?? 0) - (b.x ?? 0) * (a.y ?? 0);
  }
  return twice / 2;
}
